import { doc, updateDoc, getDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { postApi } from '@/lib/api';
import { toast } from '@/hooks/use-toast';

/**
 * Actions contextuelles par étape du pipeline staff.
 * - Les générations IA (CIM, NDA, LOI) et les transitions réelles du pipeline
 *   passent par NestJS via postApi()
 * - Les flags purement informatifs (statut d'affichage staff) passent par
 *   Firestore updateDoc()
 * - AUCUN appel à invokeFunction() / Firebase Functions
 *
 * IMPORTANT : depuis la migration vers le pipeline à 11 étapes
 * (submission, pre_evaluation, cim_creation, payment_pending, published,
 * lender_submission, nda_signed, buyer_contact, first_meeting, loi_signed,
 * closing), la publication du deal et l'envoi aux prêteurs se font
 * AUTOMATIQUEMENT via le webhook Stripe (PaymentsService.handleWebhook ->
 * DealsService.onApprovalFeePaid) une fois le vendeur/courtier a payé les
 * 2 449 $. Il n'y a donc plus de bouton staff "Publier" ou "Envoyer aux
 * prêteurs" à ce stade — ces actions ne sont plus des étapes manuelles.
 */

// ── Types ─────────────────────────────────────────────────────────────────────

export interface StaffUserContext {
  uid: string;
  email?: string | null;
  role?: string | null;
}

type Ctx = { dealId: string; lang: 'en' | 'fr'; userCtx: StaffUserContext };

// ── Helpers ───────────────────────────────────────────────────────────────────

async function patchDeal(dealId: string, data: Record<string, any>) {
  const payload = { ...data, updatedAt: serverTimestamp() };

  // Le deal peut vivre dans 'deals' OU 'dealSubmissions' selon son origine.
  // On tente les deux : celui qui existe est mis à jour, l'autre est ignoré.
  const targets = ['deals', 'dealSubmissions'] as const;
  const results = await Promise.allSettled(
    targets.map(async (col) => {
      const ref = doc(db, col, dealId);
      const snap = await getDoc(ref);
      if (!snap.exists()) return false;
      await updateDoc(ref, payload);
      return true;
    })
  );

  const updated = results.some(r => r.status === 'fulfilled' && r.value === true);
  if (!updated) {
    throw new Error(`Deal ${dealId} introuvable dans deals/ ni dealSubmissions/`);
  }
}

/**
 * Écrit un événement dans `dealAuditLog`.
 * Ne throw jamais — un log qui échoue ne doit pas casser l'action métier.
 */
export async function logDealEvent(
  dealId: string,
  action: string,
  details: Record<string, unknown> | undefined,
  userCtx: StaffUserContext,
): Promise<void> {
  try {
    await addDoc(collection(db, 'dealAuditLog'), {
      dealId,
      action,
      performedBy: userCtx.uid,
      performedByEmail: userCtx.email ?? null,
      performedByRole: userCtx.role ?? null,
      details: details ?? {},
      createdAt: serverTimestamp(),
    });
  } catch (err) {
    console.error('[logDealEvent] failed to write audit entry', { dealId, action, err });
  }
}

function ok(title: string, description?: string) {
  toast({ title, description });
}

function ko(title: string, e: unknown) {
  toast({
    title,
    description: e instanceof Error ? e.message : String(e),
    variant: 'destructive',
  });
}

// ── SUBMISSION ────────────────────────────────────────────────────────────────

export async function preEvaluate({ dealId, lang, userCtx }: Ctx) {
  try {
    await patchDeal(dealId, { status: 'pre_evaluation', stage: 'pre_evaluation' });
    await logDealEvent(dealId, 'deal.pre_evaluated', { toStage: 'pre_evaluation' }, userCtx);
    ok(lang === 'fr' ? 'Pré-évaluation lancée' : 'Pre-evaluation started');
  } catch (e) { ko(lang === 'fr' ? 'Erreur' : 'Error', e); }
}

export async function requestMoreInfo({ dealId, lang, userCtx }: Ctx) {
  try {
    await patchDeal(dealId, { status: 'needs_more_info' });
    await logDealEvent(dealId, 'deal.info_requested', { message: 'Staff requested additional information' }, userCtx);
    ok(lang === 'fr' ? 'Demande envoyée au vendeur' : 'Request sent to seller');
  } catch (e) { ko(lang === 'fr' ? 'Erreur' : 'Error', e); }
}

export async function rejectDeal({ dealId, lang, userCtx }: Ctx) {
  if (!confirm(lang === 'fr' ? 'Rejeter ce deal ?' : 'Reject this deal?')) return;
  try {
    await patchDeal(dealId, { status: 'rejected', stage: 'rejected' });
    await logDealEvent(dealId, 'deal.rejected', { reason: 'Rejected by staff' }, userCtx);
    ok(lang === 'fr' ? 'Deal rejeté' : 'Deal rejected');
  } catch (e) { ko(lang === 'fr' ? 'Erreur' : 'Error', e); }
}

// ── PRE_EVALUATION ────────────────────────────────────────────────────────────

export async function generateCIM({ dealId, lang, userCtx }: Ctx) {
  try {
    await postApi('/ai/generate-cim', { dealId });
    // Fait avancer le vrai pipeline PRE_EVALUATION -> CIM_CREATION (en plus
    // du flag d'affichage local), pour que currentStage reste la source
    // de vérité synchronisée.
    await postApi(`/deals/${dealId}/approve`, {});
    await patchDeal(dealId, { status: 'evaluation_in_progress', stage: 'cim_creation' });
    await logDealEvent(dealId, 'cim.generated', { source: 'staff_action' }, userCtx);
    ok(lang === 'fr' ? 'CIM en génération' : 'CIM generation started');
  } catch (e) { ko(lang === 'fr' ? 'Erreur génération CIM' : 'CIM generation error', e); }
}

// ── CIM_CREATION ──────────────────────────────────────────────────────────────

/**
 * Envoie le CIM au vendeur/courtier pour approbation.
 * Appelle le vrai endpoint DealApprovalService.sendForApproval, qui :
 * - crée un enregistrement dans `dealApprovals`
 * - écrit `approvalPending: true` sur le deal (c'est CE champ que
 *   SellerDashboard.tsx lit pour afficher CIMApprovalBanner — pas `status`)
 * - envoie les vrais emails au vendeur et au courtier
 */
export async function sendCIMToSeller({ dealId, lang, userCtx }: Ctx) {
  try {
    await postApi(`/deals/${dealId}/send-for-approval`, {});
    await patchDeal(dealId, { status: 'awaiting_seller_approval' });
    await logDealEvent(dealId, 'cim.sent_to_seller', {}, userCtx);
    ok(lang === 'fr' ? 'CIM envoyé au vendeur' : 'CIM sent to seller');
  } catch (e) { ko(lang === 'fr' ? 'Erreur' : 'Error', e); }
}

// ── PAYMENT_PENDING / PUBLISHED / LENDER_SUBMISSION ──────────────────────────
// Ces étapes sont maintenant automatiques (webhook Stripe -> onApprovalFeePaid).
// Pas d'action staff manuelle ici — voir resolveActions() dans
// StaffDealRowActions.tsx, qui n'affiche plus de bouton pour ces stages,
// seulement un statut informatif ("En attente du paiement du vendeur", etc.)

/**
 * Filet de sécurité manuel : si le matching prêteurs a échoué après paiement
 * (le deal reste bloqué à 'published' au lieu d'avancer à
 * 'lender_submission' — voir le catch dans onApprovalFeePaid côté NestJS),
 * le staff peut relancer le matching manuellement depuis ce bouton.
 */
export async function retryLenderMatching({ dealId, lang, userCtx }: Ctx) {
  try {
    await postApi(`/deals/${dealId}/approval-paid`, {});
    await logDealEvent(dealId, 'financing.matching_retried', {}, userCtx);
    ok(lang === 'fr' ? 'Nouvelle tentative de distribution aux prêteurs' : 'Retrying lender distribution');
  } catch (e) { ko(lang === 'fr' ? 'Erreur' : 'Error', e); }
}

// Note : il n'y a PAS d'action staff "Envoyer NDA". Le NDA est toujours en
// self-service :
// - Acheteur : signe depuis le Deal Monitor public (ListingDetail.tsx ->
//   /tools/nda-generator?recipientType=buyer)
// - Prêteur : reçoit automatiquement un email après le paiement du vendeur
//   (LenderMatchingService.onDealApprovalPaid(), déclenché par
//   onApprovalFeePaid) l'invitant à signer le NDA — aucune action staff
//   requise. Le lien de cet email doit pointer vers
//   /tools/nda-generator?deal={dealId}&recipientType=lender (à vérifier
//   côté composant du tableau de bord prêteur, pas encore examiné).
// Les deux utilisent le même template NDADocument.tsx (pas d'IA).

// ── NDA_SIGNED ────────────────────────────────────────────────────────────────

export async function shareWithBuyer({ dealId, lang, userCtx }: Ctx) {
  try {
    await patchDeal(dealId, { sharedWithBuyer: true, sharedAt: serverTimestamp() });
    await logDealEvent(dealId, 'deal.shared_with_buyer', { buyerId: null }, userCtx);
    ok(lang === 'fr' ? "Partagé avec l'acheteur" : 'Shared with buyer');
  } catch (e) { ko(lang === 'fr' ? 'Erreur' : 'Error', e); }
}

export async function scheduleMeeting({ dealId, lang, userCtx }: Ctx) {
  await logDealEvent(dealId, 'meeting.scheduled', { meetingId: null, at: new Date().toISOString() }, userCtx);
  window.location.href = `/staff/deals/${dealId}?tab=messages&action=schedule`;
}

// ── FIRST_MEETING (anciennement loi_pending) ─────────────────────────────────

export async function generateLOI({ dealId, lang, userCtx }: Ctx) {
  try {
    await postApi('/ai/generate-loi', { dealId });
    await patchDeal(dealId, { loiStatus: 'generated' });
    await logDealEvent(dealId, 'loi.generated', {}, userCtx);
    ok(lang === 'fr' ? 'LOI générée' : 'LOI generated');
  } catch (e) { ko(lang === 'fr' ? 'Erreur LOI' : 'LOI error', e); }
}

export async function trackLOISignature({ dealId, lang, userCtx }: Ctx) {
  await logDealEvent(dealId, 'loi.signature_tracked', { status: 'tracking' }, userCtx);
  window.location.href = `/staff/deals/${dealId}?tab=documents&doc=loi`;
}

// ── CLOSING ───────────────────────────────────────────────────────────────────

export async function generateInvoice({ dealId, lang, userCtx }: Ctx) {
  try {
    const res = await postApi('/payments/generate-invoice', { dealId });
    await logDealEvent(dealId, 'invoice.generated', { amount: res?.totalFees, currency: 'USD' }, userCtx);
    ok(lang === 'fr' ? 'Facture générée' : 'Invoice generated');
  } catch (e) { ko(lang === 'fr' ? 'Erreur facture' : 'Invoice error', e); }
}

export async function markClosed({ dealId, lang, userCtx }: Ctx) {
  if (!confirm(lang === 'fr' ? 'Marquer ce deal comme clôturé ?' : 'Mark this deal as closed?')) return;
  try {
    const closedAt = new Date().toISOString();
    await patchDeal(dealId, { status: 'closed', stage: 'closed', closedAt: serverTimestamp() });
    await logDealEvent(dealId, 'deal.closed', { closedAt }, userCtx);
    ok(lang === 'fr' ? 'Deal clôturé' : 'Deal closed');
  } catch (e) { ko(lang === 'fr' ? 'Erreur' : 'Error', e); }
}